import React, { useMemo, useCallback } from 'react';
import { Box, Typography, Button, Alert, Stack, TextField, InputAdornment } from '@mui/material';
import {
  Add as AddIcon,
  Help as HelpIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
  Selection,
  VirtualScroll,
  Sort,
} from '@syncfusion/ej2-react-grids';

import { useAppSelector } from '../../../../../../app/store';
import { useGetDrgsByFeeScheduleIdQuery, selectCurrentFeeSchedule } from '../../../../../../services/ContractService';
import { useSignalR } from '../../../../../../hooks/useSignalR';
import { AddEditDrgDialog } from '../../../dialogs';
import DrgUploadCsv from '../../../commands/contractCommands/DrgUploadCsv';
import DrgDownloadCsv from '../../../commands/contractCommands/DrgDownloadCsv';
import DrgHelpDialog from '../../../dialogs/contractDialogs/DrgHelpDialog';
import DrgUploadProgressDialog from '../../../dialogs/contractDialogs/DrgUploadProgress';
import { useDrgSearch, useDrgDialog, useDrgUpload } from './hooks';
import { rateTemplate, weightTemplate, losTemplate } from './templates';
import { DrgView, GrouperView } from '../../../../../../services/ContractService/types/contractTypes';

export interface DrgCodesGridProps {
  testId?: string;
}

/**
 * DrgCodesGrid - Displays and manages DRG codes for a fee schedule
 *
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for search, dialog, and upload handling
 * - SignalR integration for upload progress
 */
export const DrgCodesGrid: React.FC<DrgCodesGridProps> = ({ testId }) => {
  const selectedFeeSchedule = useAppSelector(selectCurrentFeeSchedule);

  // Fetch DRG codes
  const { data: drgs = [], isLoading, isError, error } = useGetDrgsByFeeScheduleIdQuery(
    { feeScheduleId: selectedFeeSchedule?.id || '' },
    { skip: !selectedFeeSchedule?.id }
  );

  // Custom hooks
  const { searchTerm, filteredDrgs, handleSearchChange, handleClearSearch } = useDrgSearch(drgs);

  const {
    dialogOpen,
    editingDrg,
    helpDialogOpen,
    handleAddDrg,
    handleEditDrg,
    handleCloseDialog,
    handleOpenHelpDialog,
    handleCloseHelpDialog,
  } = useDrgDialog();

  const {
    uploadProgress,
    isUploadInProgress,
    onProgress,
    onCompleted,
    onError,
    handleCloseProgressDialog,
    handleStartUpload,
  } = useDrgUpload();

  // SignalR connection
  const { isConnected: isSignalRConnected } = useSignalR({
    serviceName: 'Contracts',
    uploadType: 'Drg',
    onProgress,
    onCompleted,
    onError,
    autoConnect: true,
  });

  // Calculate rate for each DRG based on grouper and weight
  const calculateDrgRate = useCallback(
    (drg: DrgView) => {
      if (!selectedFeeSchedule?.groupers || !drg.grouperCode || !drg.weight) {
        return 0;
      }

      const matchingGrouper = selectedFeeSchedule.groupers.find(
        (grouper: GrouperView) => grouper.code === drg.grouperCode
      );

      if (!matchingGrouper || !matchingGrouper.rate) {
        return 0;
      }

      return matchingGrouper.rate * drg.weight;
    },
    [selectedFeeSchedule?.groupers]
  );

  // Handle row selection for editing
  const handleRowSelection = useCallback(
    (args: { data?: { id: string } }) => {
      if (args.data) {
        const selectedDrg = filteredDrgs.find((d) => d.id === args.data!.id);
        if (selectedDrg) {
          handleEditDrg(selectedDrg);
        }
      }
    },
    [filteredDrgs, handleEditDrg]
  );

  // Transform data for grid display
  const gridData = useMemo(() => {
    return filteredDrgs
      .map((drg) => ({
        id: drg.id,
        code: drg.code.padStart(3, '0'), // Pad DRG codes to 3 characters with leading zeros
        title: drg.title,
        weight: drg.weight,
        arithmeticMeanLOS: drg.arithmeticMeanLOS,
        grouperCode: drg.grouperCode,
        calculatedRate: calculateDrgRate(drg),
      }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [filteredDrgs, calculateDrgRate]);

  const gridSettings = {
    allowPaging: true,
    allowSorting: true,
    enableVirtualization: true,
    pageSettings: { pageSize: 100, pageSizes: [50, 100, 200, 500] },
    selectionSettings: { type: 'Single' as const, mode: 'Row' as const },
    rowHeight: 32,
  };

  if (!selectedFeeSchedule) {
    return (
      <Alert severity="warning">Please select a fee schedule to view DRG codes.</Alert>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        Error loading DRG codes: {error && 'message' in error ? (error as any).message : 'Unknown error'}
      </Alert>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}
        >
          DRG Codes ({filteredDrgs.length}{searchTerm && ` of ${drgs.length}`})
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddDrg} size="small">
            Add DRG Code
          </Button>
          <DrgUploadCsv
            disabled={isLoading || isUploadInProgress}
            onUploadStart={handleStartUpload}
          />
          <DrgDownloadCsv disabled={isLoading || drgs.length === 0} />
          <Button variant="outlined" startIcon={<HelpIcon />} onClick={handleOpenHelpDialog} size="small">
            Help
          </Button>
        </Stack>
      </Stack>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SearchIcon sx={{ color: '#64748b' }} />
          <TextField
            fullWidth
            size="small"
            placeholder="Search by code, title, grouper, weight, or LOS..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <ClearIcon
                    sx={{ cursor: 'pointer', color: '#64748b' }}
                    onClick={handleClearSearch}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f8fafc',
                '&:hover': { backgroundColor: '#f1f5f9' },
              },
            }}
          />
        </Stack>
      </Box>

      {drgs.length > 0 && (
        <Typography
          variant="body2"
          sx={{ color: '#64748b', fontStyle: 'italic', mb: 1, fontSize: '0.75rem' }}
        >
          Click on any row to edit or delete a DRG code
        </Typography>
      )}

      {gridData.length === 0 ? (
        <Alert severity="info">
          {searchTerm
            ? 'No DRG codes match your search criteria.'
            : 'No DRG codes defined yet. Click "Add DRG Code" to create your first DRG code.'}
        </Alert>
      ) : (
        <Box
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <GridComponent
            dataSource={gridData}
            {...gridSettings}
            height="350"
            width="100%"
            rowSelected={handleRowSelection}
            style={{ border: 'none' }}
            data-testid={testId}
          >
            <ColumnsDirective>
              <ColumnDirective field="code" headerText="Code" width="80" textAlign="Left" />
              <ColumnDirective field="title" headerText="Title" width="250" textAlign="Left" />
              <ColumnDirective field="grouperCode" headerText="Grouper" width="100" textAlign="Left" />
              <ColumnDirective
                field="weight"
                headerText="Weight"
                width="100"
                textAlign="Right"
                template={weightTemplate}
              />
              <ColumnDirective
                field="arithmeticMeanLOS"
                headerText="Avg LOS"
                width="100"
                textAlign="Right"
                template={losTemplate}
              />
              <ColumnDirective
                field="calculatedRate"
                headerText="Rate"
                width="120"
                textAlign="Right"
                template={rateTemplate}
              />
            </ColumnsDirective>
            <Inject services={[Page, Selection, VirtualScroll, Sort]} />
          </GridComponent>
        </Box>
      )}

      {/* Dialogs */}
      <AddEditDrgDialog
        open={dialogOpen}
        editingDrg={editingDrg}
        feeScheduleId={selectedFeeSchedule?.id || ''}
        onClose={handleCloseDialog}
      />

      <DrgHelpDialog open={helpDialogOpen} onClose={handleCloseHelpDialog} />

      {uploadProgress && (
        <DrgUploadProgressDialog
          open={!!uploadProgress}
          progress={uploadProgress?.progressPercentage || 0}
          status={uploadProgress?.message || 'Preparing upload...'}
          error={uploadProgress?.status === 'Failed' ? uploadProgress.message : undefined}
          success={uploadProgress?.progressPercentage === 100}
          uploadCount={uploadProgress?.processedRecords}
          totalCount={uploadProgress?.totalRecords}
          onClose={handleCloseProgressDialog}
        />
      )}
    </Box>
  );
};

export default DrgCodesGrid;
